import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Link,
  Button,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Stack,
  Divider,
  useDisclosure,
  HStack,
  Switch,
  useToast,
  Select,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Textarea,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { FiFilter, FiUsers, FiChevronDown } from 'react-icons/fi';
import { BiGridAlt } from 'react-icons/bi';
import SmartFilterBar from 'components/SmartFilterBar';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import dayjs from 'dayjs';
import { downloadExcel } from 'react-export-table-to-excel';
import AttendanceReportModal from './asistencia/AttendanceReportModal';
import EnrollSubjectsFromPaymentModal from './filtro-students-components/EnrollSubjectsFromPaymentModal';

const PAGE_SIZE = 10;


function getCurrentPeriodDateRange() {
  const currentYear = dayjs().year();
  const now = dayjs();

  const ranges = [
    {
      value: 'P1',
      label: 'Periodo 1',
      from: dayjs(`${currentYear}-01-01`).startOf('day'),
      to: dayjs(`${currentYear}-03-30`).endOf('day'),
    },
    {
      value: 'P2',
      label: 'Periodo 2',
      from: dayjs(`${currentYear}-04-01`).startOf('day'),
      to: dayjs(`${currentYear}-06-15`).endOf('day'),
    },
    {
      value: 'P3',
      label: 'Periodo 3',
      from: dayjs(`${currentYear}-07-15`).startOf('day'),
      to: dayjs(`${currentYear}-08-30`).endOf('day'),
    },
    {
      value: 'P4',
      label: 'Periodo 4',
      from: dayjs(`${currentYear}-09-01`).startOf('day'),
      to: dayjs(`${currentYear}-12-30`).endOf('day'),
    },
  ];

  const activePeriod = ranges.find((range) => {
    return now.valueOf() >= range.from.valueOf() && now.valueOf() <= range.to.valueOf();
  });

  if (!activePeriod) return null;

  return {
    ...activePeriod,
    fromIso: activePeriod.from.toISOString(),
    toIso: activePeriod.to.toISOString(),
    year: currentYear,
  };
}

function isDateInRange(dateValue, fromIso, toIso) {
  if (!dateValue) return false;

  const date = dayjs(dateValue);
  const from = dayjs(fromIso);
  const to = dayjs(toIso);

  if (!date.isValid()) return false;

  return date.valueOf() >= from.valueOf() && date.valueOf() <= to.valueOf();
}

function getInitials(student) {
  const u = student?.user || {};
  const first = (u.first_name || '').trim().charAt(0) || '';
  const last = (u.last_name || '').trim().charAt(0) || '';
  return (first + last).toUpperCase() || '?';
}

function getFullName(student) {
  const u = student?.user || {};
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
}

// Tipos de programa que muestran las columnas académicas (colegio, simat, último grado).
const ACADEMIC_COLUMN_SLUGS = new Set([
  'primaria-anual-para-ninos',
  'bachillerato-anual-para-adolescentes',
  'bachillerato-anual-de-usa-para-adolescentes',
  'bachillerato-flexible',
  'bachillerato-acelerado-para-adultos',
  'bachillerato-semestralizado-para-adolescentes',
]);

function calcAge(birthdate) {
  if (!birthdate) return '—';
  const b = dayjs(birthdate);
  if (!b.isValid()) return '—';
  const age = dayjs().diff(b, 'year');
  return age >= 0 && age < 130 ? age : '—';
}

function titleCase(value) {
  const v = String(value || '').replace(/_/g, ' ').trim();
  if (!v) return '';
  return v.replace(/\b\w/g, (c) => c.toUpperCase());
}

function docAbbr(name) {
  const n = (name || '').toLowerCase().trim();
  if (!n) return 'N/A';
  if (n.includes('cédula de ciudadanía') || n.includes('cedula de ciudadania')) return 'CC';
  if (n.includes('cédula de extranjería') || n.includes('cedula de extranjeria')) return 'CE';
  if (n.includes('tarjeta de identidad')) return 'TI';
  if (n.includes('pasaporte')) return 'PA';
  if (n.includes('licencia de conducir')) return 'LC';
  if (n.includes('registro civil')) return 'RC';
  if (n.includes('permiso especial de permanencia')) return 'PEP';
  return name;
}

function translateReqStatus(status) {
  const s = (status || '').toLowerCase();
  if (s === 'denied') return 'Denegado';
  if (s === 'approved') return 'Aprobado';
  if (s === 'pending') return 'Pendiente';
  return status || 'N/A';
}

function stripHtml(html) {
  if (!html) return '';
  return String(html).replace(/<[^>]*>/g, '').trim();
}

function getRequirementNames(req) {
  const names = [];

  const many = req?.requirements_many || [];
  many.forEach((r) => {
    const name = stripHtml(r?.name);
    if (name) names.push(name);
  });

  if (req?.requirement?.name) {
    const name = stripHtml(req.requirement.name);
    if (name) names.push(name);
  }

  const relName = stripHtml(req?.requirement?.data?.attributes?.name);
  if (relName) names.push(relName);

  return names.length ? names : ['(Sin nombre)'];
}

function normalizeParentesco(value) {
  const rel = String(value || '').trim().toLowerCase();

  if (!rel) return '';
  if (rel === 'madre' || rel === 'mamá' || rel === 'mama') return 'madre';
  if (rel === 'padre' || rel === 'papá' || rel === 'papa') return 'padre';
  if (rel === 'otro' || rel === 'otros') return 'otro';

  return rel;
}

function mapParent(item) {
  const a = item?.attributes || item || {};

  return {
    id: item?.id || a?.id,
    parentesco: a?.parentesco || '',
    full_name: a?.full_name || '',
    email: a?.email || '',
    home_phone: a?.home_phone || '',
    identity: a?.identity || '',
    state: a?.state || '',
    city: a?.city || '',
    address: a?.address || '',
    user: a?.user?.data?.attributes || a?.user || null,
    identity_type: a?.identity_type?.data?.attributes || a?.identity_type || null,
    students:
      a?.students?.data?.map((s) => ({
        id: s?.id,
        ...(s?.attributes || {}),
      })) ||
      a?.students ||
      [],
  };
}

function classifyParents(parents = []) {
  let madre = null;
  let padre = null;
  let otro = null;
  let sinParentesco = null;

  parents.forEach((parent) => {
    const rel = normalizeParentesco(parent?.parentesco);

    if (rel === 'madre' && !madre) madre = parent;
    else if (rel === 'padre' && !padre) padre = parent;
    else if (rel === 'otro' && !otro) otro = parent;
    else if (!rel && !sinParentesco) sinParentesco = parent;
  });

  if (!otro && sinParentesco) {
    otro = sinParentesco;
  }

  return { madre, padre, otro };
}

function getApiList(res) {
  const payload = res?.data ?? res;
  return Array.isArray(payload) ? payload : payload?.data || [];
}

function getApiAttrs(item) {
  return item?.attributes || item || {};
}

function getApiId(item) {
  return item?.id || item?.data?.id || item?.attributes?.id || null;
}

function getRelationId(rel) {
  if (!rel) return null;
  if (typeof rel === 'number') return rel;
  if (typeof rel === 'string') return Number(rel);
  if (rel?.id) return rel.id;
  if (rel?.data?.id) return rel.data.id;
  return null;
}

function getRelationList(rel) {
  if (!rel) return [];
  if (Array.isArray(rel)) return rel;
  if (Array.isArray(rel?.data)) return rel.data;
  return [];
}

function appendStudentInFilters(query, studentIds) {
  studentIds.forEach((sid, index) => {
    query.append(`filters[student][id][$in][${index}]`, String(sid));
  });
}

function getAbsenceColorScheme(count = 0) {
  if (count >= 10) return 'red';
  if (count >= 8) return 'pink';
  if (count >= 6) return 'orange';
  if (count >= 4) return 'yellow';
  if (count >= 2) return 'green';
  return 'gray';
}

async function enrichStudentsWithAbsenceCounts(rows = []) {
  const currentPeriodRange = getCurrentPeriodDateRange();

  const rowsWithEmptyCounts = rows.map((row) => ({
    ...row,
    currentAbsencePeriod: currentPeriodRange?.value || '',
    currentAbsencePeriodLabel: currentPeriodRange?.label || 'Sin periodo activo',
    currentAbsencePeriodYear: currentPeriodRange?.year || dayjs().year(),
    totalAbsencesCount: 0,
    excusedAbsencesCount: 0,
    unexcusedAbsencesCount: 0,
  }));

  if (!currentPeriodRange) {
    return rowsWithEmptyCounts;
  }

  const studentIds = [
    ...new Set(
      rows
        .map((row) => Number(row?.student?.id))
        .filter((id) => !Number.isNaN(id) && id > 0)
    ),
  ];

  if (!studentIds.length) return rowsWithEmptyCounts;

  const attendancesQuery = new URLSearchParams();
  appendStudentInFilters(attendancesQuery, studentIds);
  attendancesQuery.set('filters[live_class][start_date][$gte]', currentPeriodRange.fromIso);
  attendancesQuery.set('filters[live_class][start_date][$lte]', currentPeriodRange.toIso);
  attendancesQuery.set('populate[student]', '*');
  attendancesQuery.set('populate[live_class]', '*');
  attendancesQuery.set('pagination[pageSize]', '1000');

  const excusesQuery = new URLSearchParams();
  appendStudentInFilters(excusesQuery, studentIds);
  excusesQuery.set('populate[student]', '*');
  excusesQuery.set('populate[live_classes]', '*');
  excusesQuery.set('pagination[pageSize]', '1000');

  const [attendancesRes, excusesRes] = await Promise.all([
    xhr(`/student-attendances?${attendancesQuery.toString()}`),
    xhr(`/student-excuses?${excusesQuery.toString()}`),
  ]);

  const attendances = getApiList(attendancesRes);
  const excuses = getApiList(excusesRes);

  const excusedPairs = new Set();

  excuses.forEach((item) => {
    const excuse = getApiAttrs(item);
    const studentId = Number(getRelationId(excuse.student));

    if (!studentId) return;

    const liveClasses = getRelationList(excuse.live_classes);

    liveClasses.forEach((liveClassItem) => {
      const liveClassId = Number(getApiId(liveClassItem));

      if (liveClassId) {
        excusedPairs.add(`${studentId}:${liveClassId}`);
      }
    });
  });

  const countsByStudent = {};

  studentIds.forEach((studentId) => {
    countsByStudent[studentId] = {
      currentAbsencePeriod: currentPeriodRange.value,
      currentAbsencePeriodLabel: currentPeriodRange.label,
      currentAbsencePeriodYear: currentPeriodRange.year,
      totalAbsencesCount: 0,
      excusedAbsencesCount: 0,
      unexcusedAbsencesCount: 0,
    };
  });

  attendances.forEach((item) => {
    const attendance = getApiAttrs(item);
    const studentId = Number(getRelationId(attendance.student));
    const liveClassId = Number(getRelationId(attendance.live_class));

    const liveClassData =
      attendance?.live_class?.data?.attributes ||
      attendance?.live_class ||
      {};

    const attendanceDate =
      liveClassData?.start_date ||
      attendance?.date_time ||
      attendance?.createdAt ||
      '';

    if (!isDateInRange(attendanceDate, currentPeriodRange.fromIso, currentPeriodRange.toIso)) {
      return;
    }

    if (!studentId || !countsByStudent[studentId]) return;

    const isExcused = liveClassId
      ? excusedPairs.has(`${studentId}:${liveClassId}`)
      : false;

    countsByStudent[studentId].totalAbsencesCount += 1;

    if (isExcused) {
      countsByStudent[studentId].excusedAbsencesCount += 1;
    } else {
      countsByStudent[studentId].unexcusedAbsencesCount += 1;
    }
  });

  return rows.map((row) => {
    const studentId = Number(row?.student?.id);
    const counts = countsByStudent[studentId] || {
      currentAbsencePeriod: currentPeriodRange.value,
      currentAbsencePeriodLabel: currentPeriodRange.label,
      currentAbsencePeriodYear: currentPeriodRange.year,
      totalAbsencesCount: 0,
      excusedAbsencesCount: 0,
      unexcusedAbsencesCount: 0,
    };

    return {
      ...row,
      ...counts,
    };
  });
}

function FiltroStudents() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => ({
    program_type: searchParams.get('program_type') || '',
    program: searchParams.get('program') || '',
    group: searchParams.get('group') || '',
    status: searchParams.get('status') || '',
    semester: searchParams.get('semester') || '',
    period: searchParams.get('period') || '',
  }));

  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(() => {
    const p = searchParams.get('page');
    const n = parseInt(p, 10);
    return n >= 1 ? n : 1;
  });

  const [downloading, setDownloading] = useState(false);
  const [downloadingPayments, setDownloadingPayments] = useState(false);

  const toast = useToast();
  const [updatingBoletinId, setUpdatingBoletinId] = useState(null);

  const handleToggleBoletin = async (studentId, currentValue) => {
    if (!studentId) return;

    try {
      setUpdatingBoletinId(studentId);

      const nextValue = !currentValue;

      await xhr.put(`/students/${studentId}`, {
        data: {
          boletin: nextValue,
        },
      });

      setStudents((prev) =>
        prev.map((row) =>
          row?.student?.id === studentId
            ? {
              ...row,
              student: {
                ...row.student,
                boletin: nextValue,
              },
            }
            : row
        )
      );

      toast({
        title: 'Boletín actualizado',
        description: `El boletín fue ${nextValue ? 'activado' : 'desactivado'} correctamente.`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error actualizando boletín:', error?.response?.data || error);

      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado del boletín.',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setUpdatingBoletinId(null);
    }
  };

  const hasMinimumFilters = Boolean(filters.program_type);

  const syncFiltersToUrl = useCallback(
    (nextFilters, page) => {
      const next = new URLSearchParams();

      if (nextFilters.program_type) next.set('program_type', nextFilters.program_type);
      if (nextFilters.program) next.set('program', nextFilters.program);
      if (nextFilters.group) next.set('group', nextFilters.group);
      if (nextFilters.status) next.set('status', nextFilters.status);
      if (nextFilters.semester) next.set('semester', nextFilters.semester);
      if (nextFilters.period) next.set('period', nextFilters.period);
      if (page > 1) next.set('page', String(page));

      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const loadStudents = useCallback(async () => {
    if (!hasMinimumFilters) return;

    try {
      setLoading(true);

      const params = new URLSearchParams({
        program_type: filters.program_type,
        page: String(currentPage),
        pageSize: String(PAGE_SIZE),
      });

      if (filters.program) params.set('program', filters.program);
      if (filters.semester) params.set('semester', filters.semester);
      if (filters.group) params.set('group', filters.group);
      if (filters.status) params.set('status', filters.status);
      if (filters.period) params.set('period', filters.period);

      const res = await xhr(`/students/generalFilter?${params.toString()}`);
      const data = res.data || res;
      const baseRows = data.students || [];

      const enrichedRows = await enrichStudentsWithAbsenceCounts(baseRows);

      setStudents(enrichedRows);
      setTotal(data.total ?? 0);
      setPageCount(data.pageCount ?? 0);
    } catch (err) {
      console.error('Error loading students:', err);
      setStudents([]);
      setTotal(0);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  }, [hasMinimumFilters, filters, currentPage]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const isTechnicalCareerProgramType =
    filters.program_type === 'technical-career' ||
    filters.program_type === 'technical_career' ||
    filters.program_type === 'carreras-tecnicas';

  const showAcademicColumns = ACADEMIC_COLUMN_SLUGS.has(filters.program_type);

  const handleFilterChange = useCallback(
    (newFilters) => {
      const normalizedFilters = { ...newFilters };

      const isTechnicalCareer =
        normalizedFilters.program_type === 'technical-career' ||
        normalizedFilters.program_type === 'technical_career' ||
        normalizedFilters.program_type === 'carreras-tecnicas';

      if (!isTechnicalCareer) {
        normalizedFilters.period = '';
      }

      setFilters(normalizedFilters);
      setCurrentPage(1);
      syncFiltersToUrl(normalizedFilters, 1);
    },
    [syncFiltersToUrl]
  );

  const handlePageChange = useCallback(
    (event) => {
      const page = event.selected + 1;
      setCurrentPage(page);
      syncFiltersToUrl(filters, page);
    },
    [filters, syncFiltersToUrl]
  );

  const { isOpen: isParentOpen, onOpen: onParentOpen, onClose: onParentClose } = useDisclosure();
  const [parentLoading, setParentLoading] = useState(false);
  const [parentData, setParentData] = useState({
    madre: null,
    padre: null,
    otro: null,
  });

  const { isOpen: isReqOpen, onOpen: onReqOpen, onClose: onReqClose } = useDisclosure();
  const [reqSelected, setReqSelected] = useState([]);

  const { isOpen: isPayOpen, onOpen: onPayOpen, onClose: onPayClose } = useDisclosure();
  const [paySelected, setPaySelected] = useState([]);

  const { isOpen: isAbsOpen, onOpen: onAbsOpen, onClose: onAbsClose } = useDisclosure();

  const {
    isOpen: isAttendanceOpen,
    onOpen: onAttendanceOpen,
    onClose: onAttendanceClose,
  } = useDisclosure();

  const [absLoading, setAbsLoading] = useState(false);
  const [absStudent, setAbsStudent] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [deletingAbsId, setDeletingAbsId] = useState(null);
  const [sendingAbsenceReport, setSendingAbsenceReport] = useState(false);
  const [selectedAbsencePeriod, setSelectedAbsencePeriod] = useState('');

  const {
    isOpen: isEnrollSubjectsOpen,
    onOpen: onEnrollSubjectsOpen,
    onClose: onEnrollSubjectsClose,
  } = useDisclosure();

  const [payStudentSelected, setPayStudentSelected] = useState(null);
  const [selectedPaymentForSubjects, setSelectedPaymentForSubjects] = useState(null);

  const { isOpen: isNotesOpen, onOpen: onNotesOpen, onClose: onNotesClose } = useDisclosure();
  const [notesStudent, setNotesStudent] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const openNotesModal = (student) => {
    setNotesStudent({ id: student?.id, name: getFullName(student) });
    setNotesValue(student?.notes || '');
    onNotesOpen();
  };

  const handleSaveNotes = async () => {
    if (!notesStudent?.id) return;

    try {
      setSavingNotes(true);

      await xhr.put(`/students/${notesStudent.id}`, {
        data: { notes: notesValue },
      });

      setStudents((prev) =>
        prev.map((row) =>
          row?.student?.id === notesStudent.id
            ? { ...row, student: { ...row.student, notes: notesValue } }
            : row
        )
      );

      toast({
        title: 'Notas actualizadas',
        description: 'Las notas del estudiante se guardaron correctamente.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });

      onNotesClose();
    } catch (error) {
      console.error('Error guardando notas:', error?.response?.data || error);

      toast({
        title: 'Error',
        description: 'No se pudieron guardar las notas.',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setSavingNotes(false);
    }
  };

  const openParentModal = async (studentId) => {
    setParentData({ madre: null, padre: null, otro: null });
    setParentLoading(true);
    onParentOpen();

    try {
      const res = await xhr(`/parents?filters[students][id][$eq]=${studentId}&populate=*`);
      const list = (res?.data?.data || []).map(mapParent);

      let madre = null;
      let padre = null;
      let otro = null;
      let sinParentesco = null;

      list.forEach((parent) => {
        const rel = normalizeParentesco(parent?.parentesco);

        if (rel === 'madre' && !madre) madre = parent;
        else if (rel === 'padre' && !padre) padre = parent;
        else if (rel === 'otro' && !otro) otro = parent;
        else if (!rel && !sinParentesco) sinParentesco = parent;
      });

      if (!otro && sinParentesco) {
        otro = sinParentesco;
      }

      setParentData({ madre, padre, otro });
    } catch (e) {
      console.error('Error cargando acudientes:', e);
      setParentData({ madre: null, padre: null, otro: null });
    } finally {
      setParentLoading(false);
    }
  };

  const sendAbsenceReport = async () => {
    if (!absStudent?.id) return;

    if (!selectedAbsencePeriod) {
      toast({
        title: 'Selecciona un periodo',
        description: 'Debes seleccionar el periodo que deseas enviar.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const confirmed = window.confirm(
      `¿Deseas enviar el reporte de inasistencias del ${selectedAbsencePeriod} de ${absStudent.name || 'este estudiante'} al estudiante y acudientes?`
    );

    if (!confirmed) return;

    setSendingAbsenceReport(true);

    try {
      await xhr.post(`/student-attendances/send-absence-report`, {
        studentId: absStudent.id,
        period: selectedAbsencePeriod,
      });

      toast({
        title: 'Reporte enviado',
        description: 'El reporte de inasistencias fue enviado correctamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error enviando reporte de inasistencias:', error);

      toast({
        title: 'No se pudo enviar el reporte',
        description:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          'Ocurrió un error al enviar el reporte.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSendingAbsenceReport(false);
    }
  };

  const parentName = (p) =>
    !p
      ? 'N/A'
      : p.full_name || `${p?.user?.first_name || ''} ${p?.user?.last_name || ''}`.trim() || 'N/A';

  const parentEmail = (p) => p?.email || p?.user?.email || 'N/A';

  const parentPhone = (p) => p?.home_phone || p?.user?.cellphone || 'N/A';

  const parentDoc = (p) => {
    const t = docAbbr(p?.identity_type?.name);
    const num = p?.identity || 'N/A';

    if (t === 'N/A' && num === 'N/A') return 'N/A';

    return `${t} ${num}`.trim();
  };

  const openReqModal = (requirements = []) => {
    setReqSelected(requirements);
    onReqOpen();
  };

  const openPayModal = (student, payments = []) => {
    setPayStudentSelected(student);
    setPaySelected(payments);
    onPayOpen();
  };

  const openAbsencesModal = async (studentId, fullName) => {
    const currentPeriodRange = getCurrentPeriodDateRange();

    setAbsStudent({ id: studentId, name: fullName || `Estudiante ${studentId}` });
    setAbsences([]);
    setAbsLoading(true);
    onAbsOpen();
    setSelectedAbsencePeriod('');

    if (!currentPeriodRange) {
      toast({
        title: 'Sin periodo activo',
        description: 'La fecha actual no está dentro de ningún periodo académico configurado.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });

      setAbsLoading(false);
      return;
    }

    try {
      const attendancesQuery = new URLSearchParams();
      attendancesQuery.set('filters[student][id][$eq]', String(studentId));
      attendancesQuery.set('filters[live_class][start_date][$gte]', currentPeriodRange.fromIso);
      attendancesQuery.set('filters[live_class][start_date][$lte]', currentPeriodRange.toIso);
      attendancesQuery.set('populate[student]', '*');
      attendancesQuery.set('populate[live_class][populate][subject]', '*');
      attendancesQuery.set('sort[0]', 'createdAt:desc');
      attendancesQuery.set('pagination[pageSize]', '200');

      const excusesQuery = new URLSearchParams();
      excusesQuery.set('filters[student][id][$eq]', String(studentId));
      excusesQuery.set('populate[live_classes]', '*');
      excusesQuery.set('pagination[pageSize]', '9999');

      const [attendancesRes, excusesRes] = await Promise.all([
        xhr(`/student-attendances?${attendancesQuery.toString()}`),
        xhr(`/student-excuses?${excusesQuery.toString()}`),
      ]);

      const attendancesList = getApiList(attendancesRes);
      const excusesList = getApiList(excusesRes);

      const excuseByLiveClassId = new Map();

      excusesList.forEach((item) => {
        const excuse = getApiAttrs(item);
        const liveClasses = getRelationList(excuse.live_classes);

        liveClasses.forEach((liveClassItem) => {
          const liveClassId = Number(getApiId(liveClassItem));

          if (liveClassId) {
            excuseByLiveClassId.set(liveClassId, {
              id: getApiId(item),
              description: excuse.description || '',
              excuse_start: excuse.excuse_start || '',
              excuse_finish: excuse.excuse_finish || '',
            });
          }
        });
      });

      const normalizedAbsences = attendancesList
        .map((item) => {
          const attendance = getApiAttrs(item);
          const liveClassId = Number(getRelationId(attendance.live_class));

          const liveClassData =
            attendance?.live_class?.data?.attributes ||
            attendance?.live_class ||
            {};

          const subjectData =
            liveClassData?.subject?.data?.attributes ||
            liveClassData?.subject ||
            {};

          const excuse = liveClassId ? excuseByLiveClassId.get(liveClassId) : null;

          return {
            id: getApiId(item),
            liveClassId,
            date_time:
              attendance?.date_time ||
              liveClassData?.start_date ||
              attendance?.createdAt ||
              '',
            details: attendance?.details || attendance?.Details || '',
            classTitle: liveClassData?.title || 'Clase',
            subjectName: subjectData?.name || '—',
            excused: Boolean(excuse),
            excuse,
            periodLabel: currentPeriodRange.label,
            periodYear: currentPeriodRange.year,
          };
        })
        .filter((absence) =>
          isDateInRange(absence.date_time, currentPeriodRange.fromIso, currentPeriodRange.toIso)
        );

      setAbsences(normalizedAbsences);
    } catch (e) {
      console.error('Error cargando inasistencias:', e);
      setAbsences([]);
    } finally {
      setAbsLoading(false);
    }
  };

  const deleteAbsence = async (absenceId) => {
    if (!absenceId || !window.confirm('¿Seguro que deseas eliminar esta inasistencia?')) return;

    const deletedAbsence = absences.find((x) => x.id === absenceId);

    setDeletingAbsId(absenceId);

    try {
      await xhr(`/student-attendances/${absenceId}`, { method: 'DELETE' });

      setAbsences((prev) => prev.filter((x) => x.id !== absenceId));

      if (absStudent?.id && deletedAbsence) {
        setStudents((prev) =>
          prev.map((row) => {
            if (Number(row?.student?.id) !== Number(absStudent.id)) return row;

            const totalAbsencesCount = Math.max((row.totalAbsencesCount || 0) - 1, 0);

            const excusedAbsencesCount = deletedAbsence.excused
              ? Math.max((row.excusedAbsencesCount || 0) - 1, 0)
              : row.excusedAbsencesCount || 0;

            const unexcusedAbsencesCount = !deletedAbsence.excused
              ? Math.max((row.unexcusedAbsencesCount || 0) - 1, 0)
              : row.unexcusedAbsencesCount || 0;

            return {
              ...row,
              totalAbsencesCount,
              excusedAbsencesCount,
              unexcusedAbsencesCount,
            };
          })
        );
      }
    } catch (e) {
      console.error('Error eliminando inasistencia:', e);
      alert('No se pudo eliminar.');
    } finally {
      setDeletingAbsId(null);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);

    try {
      const params = new URLSearchParams({
        program_type: filters.program_type,
        page: '1',
        pageSize: '999999',
      });

      if (filters.program) params.set('program', filters.program);
      if (filters.semester) params.set('semester', filters.semester);
      if (filters.group) params.set('group', filters.group);
      if (filters.status) params.set('status', filters.status);
      if (filters.period) params.set('period', filters.period);

      const resAll = await xhr(`/students/generalFilter?${params.toString()}`);
      const rows = resAll?.data?.students || [];
      const studentIds = rows.map((r) => r?.student?.id).filter(Boolean);

      let parentMap = {};

      if (studentIds.length) {
        const parentsUrlForStudentIds = (ids) => {
          const q = new URLSearchParams();

          q.set('populate', '*');

          ids.forEach((sid, index) => {
            q.append(`filters[students][id][$in][${index}]`, String(sid));
          });

          return `/parents?${q.toString()}`;
        };

        const parentsUrlForOneStudent = (sid) => {
          const q = new URLSearchParams();

          q.set('populate', '*');
          q.set('filters[students][id][$eq]', String(sid));

          return `/parents?${q.toString()}`;
        };

        const normalizeParents = (res) => {
          const payload = res?.data ?? res;
          const list = Array.isArray(payload) ? payload : payload?.data || [];

          return list.map((p) => {
            if (!p?.attributes) return p;

            const flat = { id: p.id, ...p.attributes };

            if (flat.user?.data) {
              const u = flat.user.data;
              flat.user = u?.attributes ? { id: u.id, ...u.attributes } : u;
            }

            return flat;
          });
        };

        const parentsByStudent = {};

        const addParentsFromFlat = (flatList) => {
          flatList.forEach((raw) => {
            const p = mapParent(raw);

            (p.students || []).forEach((st) => {
              if (!st?.id) return;

              if (!parentsByStudent[st.id]) parentsByStudent[st.id] = [];

              const list = parentsByStudent[st.id];

              if (!list.some((x) => x.id === p.id)) list.push(p);
            });
          });
        };

        const uniqueIds = [
          ...new Set(studentIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))),
        ];

        const IN_CHUNK = 40;

        for (let i = 0; i < uniqueIds.length; i += IN_CHUNK) {
          const chunk = uniqueIds.slice(i, i + IN_CHUNK);
          const parentsRes = await xhr(parentsUrlForStudentIds(chunk));
          addParentsFromFlat(normalizeParents(parentsRes));
        }

        const missing = uniqueIds.filter((id) => !parentsByStudent[id]?.length);

        for (const id of missing) {
          const res = await xhr(parentsUrlForOneStudent(id));
          const normalized = normalizeParents(res);

          addParentsFromFlat(normalized);

          if (!parentsByStudent[id]?.length && normalized.length) {
            const p = mapParent(normalized[0]);

            if (!parentsByStudent[id]) parentsByStudent[id] = [];

            if (!parentsByStudent[id].some((x) => x.id === p.id)) {
              parentsByStudent[id].push(p);
            }
          }
        }

        Object.keys(parentsByStudent).forEach((studentId) => {
          parentMap[studentId] = classifyParents(parentsByStudent[studentId]);
        });
      }

      const body = rows.map((r) => {
        const st = r.student || {};
        const u = st.user || {};
        const parentsByType = parentMap[st.id] || { madre: null, padre: null, otro: null };

        const madre = parentsByType.madre;
        const padre = parentsByType.padre;
        const otro = parentsByType.otro;

        const getParentName = (parent) =>
          parent?.full_name ||
          `${parent?.user?.first_name || ''} ${parent?.user?.last_name || ''}`.trim() ||
          'N/A';

        const getParentDoc = (parent) => {
          const parentDocType = docAbbr(parent?.identity_type?.name);
          const parentDocNum = parent?.identity || 'N/A';

          return parentDocType === 'N/A' && parentDocNum === 'N/A'
            ? 'N/A'
            : `${parentDocType} ${parentDocNum}`.trim();
        };

        const stDocType = docAbbr(st?.identity_type?.name);
        const stDocNum = st?.identity || 'N/A';
        const stDoc =
          stDocType === 'N/A' && stDocNum === 'N/A' ? 'N/A' : `${stDocType} ${stDocNum}`.trim();

        return [
          st?.id || 'N/A',
          `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || 'N/A',
          calcAge(u?.birthdate),
          stDoc,
          st?.expedition_place || 'N/A',
          u?.cellphone || 'N/A',
          u?.email || 'N/A',
          ...(showAcademicColumns
            ? [
                titleCase(st?.school) || 'N/A',
                titleCase(st?.simat) || 'N/A',
                st?.last_approved_grade || 'N/A',
              ]
            : []),
          st?.desease || 'N/A',
          r?.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD') : 'N/A',
          r?.last_login ? dayjs(r.last_login).format('YYYY-MM-DD') : 'N/A',
          st?.notes || 'N/A',

          getParentName(madre),
          getParentDoc(madre),
          madre?.email || madre?.user?.email || 'N/A',
          madre?.home_phone || madre?.user?.cellphone || 'N/A',
          madre?.state || 'N/A',
          madre?.city || 'N/A',
          madre?.address || 'N/A',

          getParentName(padre),
          getParentDoc(padre),
          padre?.email || padre?.user?.email || 'N/A',
          padre?.home_phone || padre?.user?.cellphone || 'N/A',
          padre?.state || 'N/A',
          padre?.city || 'N/A',
          padre?.address || 'N/A',

          getParentName(otro),
          getParentDoc(otro),
          otro?.email || otro?.user?.email || 'N/A',
          otro?.home_phone || otro?.user?.cellphone || 'N/A',
          otro?.state || 'N/A',
          otro?.city || 'N/A',
          otro?.address || 'N/A',
        ];
      });

      const header = [
        'CÓDIGO',
        'ESTUDIANTE',
        'EDAD',
        'DOC ESTUDIANTE',
        'LUGAR EXP',
        'CELULAR EST',
        'EMAIL EST',
        ...(showAcademicColumns ? ['COLEGIO', 'SIMAT', 'ÚLT. GRADO APROBADO'] : []),
        'NEE',
        'INICIO',
        'ÚLTIMA SESIÓN',
        'NOTAS',

        'MADRE',
        'DOC MADRE',
        'EMAIL MADRE',
        'TELÉFONO MADRE',
        'DEPARTAMENTO MADRE',
        'CIUDAD MADRE',
        'DIRECCIÓN MADRE',

        'PADRE',
        'DOC PADRE',
        'EMAIL PADRE',
        'TELÉFONO PADRE',
        'DEPARTAMENTO PADRE',
        'CIUDAD PADRE',
        'DIRECCIÓN PADRE',

        'OTRO ACUDIENTE',
        'DOC OTRO',
        'EMAIL OTRO',
        'TELÉFONO OTRO',
        'DEPARTAMENTO OTRO',
        'CIUDAD OTRO',
        'DIRECCIÓN OTRO',
      ];

      downloadExcel({
        fileName: `filtro-estudiantes-${dayjs().format('YYYY-MM-DD')}`,
        sheet: 'estudiantes',
        tablePayload: { header, body },
      });
    } catch (e) {
      console.error('Error descargando excel:', e);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPaymentsExcel = async () => {
    setDownloadingPayments(true);

    try {
      const params = new URLSearchParams({
        program_type: filters.program_type,
      });

      if (filters.program) params.set('program', filters.program);
      if (filters.semester) params.set('semester', filters.semester);
      if (filters.group) params.set('group', filters.group);
      if (filters.status) params.set('status', filters.status);
      if (filters.period) params.set('period', filters.period);

      const res = await xhr(`/payments/getStudentPaymentsForExport?${params.toString()}`);
      const payments = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

      const body = payments.map((p) => [
        p.student_id ?? 'N/A',
        p.student_name || 'N/A',
        p.payment_date ? dayjs(p.payment_date).format('YYYY-MM-DD') : 'N/A',
        p.amount ?? 'N/A',
        p.type || 'N/A',
        p.state || 'N/A',
        p.description || 'N/A',
      ]);

      const header = [
        'CÓDIGO',
        'ESTUDIANTE',
        'FECHA',
        'VALOR',
        'CONCEPTO',
        'ESTADO COMERCIAL',
        'DESCRIPCIÓN',
      ];

      downloadExcel({
        fileName: `pagos-estudiantes-${dayjs().format('YYYY-MM-DD')}`,
        sheet: 'pagos',
        tablePayload: { header, body },
      });
    } catch (e) {
      console.error('Error descargando excel de pagos:', e);
    } finally {
      setDownloadingPayments(false);
    }
  };

  const start = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, total);

  const tableThProps = {
    bg: 'gray.50',
    py: 3,
    px: 2,
    fontSize: 'xs',
    fontWeight: 'semibold',
    color: 'gray.700',
    borderColor: 'gray.100',
  };

  const ParentSection = ({ title, parent }) => (
    <Box p={3} bg="gray.50" rounded="md">
      <Text fontWeight="bold" mb={2}>
        {title}
      </Text>
      <Text>
        <b>Nombre:</b> {parentName(parent)}
      </Text>
      <Text>
        <b>Email:</b> {parentEmail(parent)}
      </Text>
      <Text>
        <b>Documento:</b> {parentDoc(parent)}
      </Text>
      <Divider my={2} />
      <Text>
        <b>Departamento:</b> {parent?.state || 'N/A'}
      </Text>
      <Text>
        <b>Ciudad:</b> {parent?.city || 'N/A'}
      </Text>
      <Text>
        <b>Dirección:</b> {parent?.address || 'N/A'}
      </Text>
      <Text>
        <b>Teléfono:</b> {parentPhone(parent)}
      </Text>
    </Box>
  );

  return (
    <Box p={4} maxW="100%">
      <SmartFilterBar
        title="Filtro Estudiantes"
        description="Selecciona los filtros para ver el listado de estudiantes."
        value={filters}
        onChange={handleFilterChange}
      />

      {isTechnicalCareerProgramType && (
        <Box
          mt={4}
          bg="white"
          borderRadius="lg"
          boxShadow="sm"
          borderWidth="1px"
          borderColor="gray.100"
          p={4}
        >
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
            Periodo
          </Text>

          <Select
            placeholder="Selecciona un periodo"
            value={filters.period}
            onChange={(e) => {
              const nextFilters = {
                ...filters,
                period: e.target.value,
              };

              setFilters(nextFilters);
              setCurrentPage(1);
              syncFiltersToUrl(nextFilters, 1);
            }}
            maxW="240px"
          >
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
            <option value="P4">P4</option>
          </Select>
        </Box>
      )}

      {!hasMinimumFilters && (
        <Box
          mt={6}
          p={8}
          borderRadius="xl"
          borderWidth="2px"
          borderColor="gray.200"
          borderStyle="dashed"
          bg="gray.50"
          minH="280px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Flex flexDirection="column" align="center" gap={4} maxW="400px">
            <Flex
              w={16}
              h={16}
              borderRadius="full"
              bg="#E8E4FC"
              color="#574feb"
              borderWidth="1px"
              borderColor="#D4CCF0"
              align="center"
              justify="center"
            >
              <Box as={FiFilter} boxSize={8} />
            </Flex>

            <Box textAlign="center">
              <Text fontWeight="semibold" fontSize="lg" color="gray.800" mb={2}>
                Selecciona filtros para ver estudiantes
              </Text>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">
                Elige al menos un <strong>Tipo de programa</strong> en la barra de filtros para generar el listado.
              </Text>
            </Box>
          </Flex>
        </Box>
      )}

      {hasMinimumFilters && (
        <>
          <Flex align="center" justify="space-between" flexWrap="wrap" gap={3} my={4}>
            <HStack spacing={4} align="center">
              <Tooltip label="Total de estudiantes que coinciden con los filtros." placement="top" hasArrow>
                <Flex align="center" gap={2}>
                  <Box bg="blue.100" p={2} borderRadius="full">
                    <Box as={FiUsers} color="blue.600" boxSize={5} />
                  </Box>

                  <Text fontWeight="bold" fontSize="lg">
                    {total}
                  </Text>

                  <Text fontSize="sm" color="gray.600">
                    estudiantes
                  </Text>
                </Flex>
              </Tooltip>
            </HStack>

            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                colorScheme="green"
                isLoading={downloading || downloadingPayments}
                loadingText="Exportando…"
                isDisabled={loading || !students.length}
                rightIcon={<FiChevronDown />}
              >
                Descargar
              </MenuButton>
              <MenuList zIndex={20} fontSize="sm" minW="320px" py={1}>
                <MenuItem onClick={handleDownloadExcel} _hover={{ bg: 'green.50' }}>
                  <Box>
                    <Text fontWeight="semibold">Estudiantes (Excel)</Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Listado con datos personales del estudiante y acudientes según los filtros activos.
                    </Text>
                  </Box>
                </MenuItem>
                <MenuItem onClick={handleDownloadPaymentsExcel} _hover={{ bg: 'green.50' }}>
                  <Box>
                    <Text fontWeight="semibold">Pagos por estudiante (Excel)</Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Una fila por cada pago realizado: código, nombre, fecha, valor, concepto, estado comercial y descripción. Ordenado por nombre del estudiante.
                    </Text>
                  </Box>
                </MenuItem>
                <MenuItem
                  onClick={onAttendanceOpen}
                  isDisabled={!filters.program}
                  _hover={{ bg: 'green.50' }}
                  title={!filters.program ? 'Elige un programa para activar esta descarga.' : undefined}
                >
                  <Box>
                    <Text fontWeight="semibold">Asistencia (Excel)</Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Reporte de asistencia del grupo. Requiere <strong>programa</strong> en los filtros.
                    </Text>
                  </Box>
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>

          <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" borderWidth="1px" borderColor="gray.100">
            <Box bg="#574feb" px={5} py={3}>
              <Flex align="center" gap={3}>
                <Box as={BiGridAlt} color="white" boxSize={6} />
                <Text fontWeight="bold" color="white" fontSize="lg">
                  Listado de estudiantes
                </Text>
              </Flex>
            </Box>

            {loading ? (
              <Flex justify="center" py={12}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <TableContainer overflowX="auto" maxW="100%">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th
                        position="sticky"
                        left={0}
                        zIndex={2}
                        minW="200px"
                        maxW="240px"
                        borderRightWidth="1px"
                        {...tableThProps}
                      >
                        ESTUDIANTE
                      </Th>
                      <Th {...tableThProps}>EDAD</Th>
                      <Th {...tableThProps}>DOC</Th>
                      <Th {...tableThProps}>LUGAR EXP.</Th>
                      <Th {...tableThProps}>CELULAR</Th>
                      <Th {...tableThProps}>EMAIL</Th>
                      {showAcademicColumns && (
                        <>
                          <Th {...tableThProps}>COLEGIO</Th>
                          <Th {...tableThProps}>SIMAT</Th>
                          <Th {...tableThProps}>ÚLT. GRADO</Th>
                        </>
                      )}
                      <Th {...tableThProps}>NEE</Th>
                      <Th {...tableThProps}>ACUDIENTE</Th>
                      <Th {...tableThProps}>FECHAS</Th>
                      <Th {...tableThProps}>REQUISITOS</Th>
                      <Th {...tableThProps}>PAGOS</Th>
                      <Th {...tableThProps}>BOLETÍN</Th>
                      <Th {...tableThProps}>INASIST.</Th>
                      <Th {...tableThProps}>NOTAS</Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {students.length === 0 ? (
                      <Tr>
                        <Td colSpan={showAcademicColumns ? 17 : 14} py={8} textAlign="center" color="gray.500">
                          No hay estudiantes para los filtros seleccionados.
                        </Td>
                      </Tr>
                    ) : (
                      students.map((row) => {
                        const st = row.student;
                        const docType = docAbbr(st?.identity_type?.name);
                        const docNum = st?.identity || 'N/A';
                        const doc =
                          docType === 'N/A' && docNum === 'N/A'
                            ? 'N/A'
                            : `${docType} ${docNum}`.trim();

                        const fullName = getFullName(st);
                        const unexcusedCount = row.unexcusedAbsencesCount || 0;
                        const excusedCount = row.excusedAbsencesCount || 0;
                        const totalAbsencesCount = row.totalAbsencesCount || 0;

                        return (
                          <Tr key={row.id}>
                            <Td
                              position="sticky"
                              left={0}
                              zIndex={1}
                              bg="white"
                              borderRightWidth="1px"
                              borderColor="gray.100"
                              py={2}
                              px={3}
                              maxW="240px"
                            >
                              <Flex align="center" gap={3}>
                                <Flex
                                  w={10}
                                  h={10}
                                  flexShrink={0}
                                  borderRadius="full"
                                  bg="#E8E4FC"
                                  color="#574feb"
                                  borderWidth="1px"
                                  borderColor="#D4CCF0"
                                  align="center"
                                  justify="center"
                                  fontSize="xs"
                                  fontWeight="bold"
                                >
                                  {getInitials(st)}
                                </Flex>

                                <Box minW={0}>
                                  <Tooltip label={fullName} placement="top" hasArrow openDelay={400}>
                                    <Link
                                      href={`/plataforma/administrador/ver-estudiante/${st?.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      fontWeight="medium"
                                      color="blue.600"
                                      _hover={{ underline: true }}
                                      isTruncated
                                      display="block"
                                      maxW="170px"
                                    >
                                      {fullName}
                                    </Link>
                                  </Tooltip>

                                  <Text fontSize="xs" color="gray.500">
                                    ID: {st?.id ?? '—'}
                                  </Text>
                                </Box>
                              </Flex>
                            </Td>

                            <Td py={2} px={2}>{calcAge(st?.user?.birthdate)}</Td>
                            <Td py={2} px={2}>{doc}</Td>
                            <Td py={2} px={2}>{st?.expedition_place || 'N/A'}</Td>
                            <Td py={2} px={2}>{st?.user?.cellphone || 'N/A'}</Td>
                            <Td py={2} px={2}>
                              <Tooltip label={st?.user?.email || 'N/A'} placement="top" hasArrow openDelay={400}>
                                <Text isTruncated maxW="180px">
                                  {st?.user?.email || 'N/A'}
                                </Text>
                              </Tooltip>
                            </Td>

                            {showAcademicColumns && (
                              <>
                                <Td py={2} px={2}>
                                  <Tooltip label={titleCase(st?.school) || 'N/A'} placement="top" hasArrow openDelay={400}>
                                    <Text isTruncated maxW="150px">
                                      {titleCase(st?.school) || '—'}
                                    </Text>
                                  </Tooltip>
                                </Td>
                                <Td py={2} px={2}>{titleCase(st?.simat) || '—'}</Td>
                                <Td py={2} px={2}>{st?.last_approved_grade || '—'}</Td>
                              </>
                            )}

                            <Td py={2} px={2}>
                              <Tooltip label={st?.desease || 'N/A'} placement="top" hasArrow openDelay={400}>
                                <Text isTruncated maxW="160px">
                                  {st?.desease || '—'}
                                </Text>
                              </Tooltip>
                            </Td>

                            <Td py={2} px={2}>
                              <Button size="xs" onClick={() => openParentModal(st?.id)}>
                                Ver
                              </Button>
                            </Td>

                            <Td py={2} px={2} fontSize="xs">
                              Inicio: {row.createdAt ? dayjs(row.createdAt).format('YYYY-MM-DD') : '—'}
                              <br />
                              Últ. sesión: {row.last_login ? dayjs(row.last_login).format('YYYY-MM-DD') : '—'}
                            </Td>

                            <Td py={2} px={2}>
                              <Button size="xs" onClick={() => openReqModal(row.requirements || [])}>
                                Ver ({row.requirements?.length || 0})
                              </Button>
                            </Td>

                            <Td py={2} px={2}>
                              <Button size="xs" onClick={() => openPayModal(st, row.payments || [])}>
                                Ver ({row.payments?.length || 0})
                              </Button>
                            </Td>

                            <Td py={2} px={2}>
                              <Switch
                                isChecked={Boolean(st?.boletin)}
                                onChange={() => handleToggleBoletin(st?.id, st?.boletin)}
                                isDisabled={updatingBoletinId === st?.id}
                                colorScheme="green"
                              />
                            </Td>

                            <Td py={2} px={2}>
                              <Tooltip
                                label={`${row.currentAbsencePeriodLabel || 'Periodo actual'} ${row.currentAbsencePeriodYear || dayjs().year()} | Sin excusa: ${unexcusedCount} | Excusadas: ${excusedCount} | Total: ${totalAbsencesCount}`}
                                placement="top"
                                hasArrow
                              >
                                <Button
                                  size="xs"
                                  colorScheme={getAbsenceColorScheme(unexcusedCount)}
                                  onClick={() => openAbsencesModal(st?.id, fullName)}
                                >
                                  {row.currentAbsencePeriod || 'Periodo'}: {unexcusedCount}
                                </Button>
                              </Tooltip>
                            </Td>

                            <Td py={2} px={2} maxW="220px">
                              <Flex align="center" gap={2}>
                                <Tooltip label={st?.notes || 'Sin notas'} placement="top" hasArrow openDelay={400}>
                                  <Text isTruncated maxW="130px" color={st?.notes ? 'gray.700' : 'gray.400'}>
                                    {st?.notes || '—'}
                                  </Text>
                                </Tooltip>

                                <Button size="xs" onClick={() => openNotesModal(st)}>
                                  {st?.notes ? 'Editar' : 'Agregar'}
                                </Button>
                              </Flex>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            <Flex
              justify="space-between"
              align="center"
              p={4}
              borderTopWidth="1px"
              borderColor="gray.100"
              flexWrap="wrap"
              gap={3}
            >
              <Text color="gray.600" fontSize="sm">
                Mostrando {start}-{end} de {total} estudiantes
              </Text>

              {pageCount > 1 && (
                <ReactPaginate
                  breakLabel="..."
                  nextLabel=">"
                  previousLabel="<"
                  onPageChange={handlePageChange}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageCount={pageCount}
                  forcePage={currentPage - 1}
                  renderOnZeroPageCount={null}
                  containerClassName="filtroStudentsPagination"
                  pageClassName="filtroStudentsPaginationItem"
                  activeClassName="filtroStudentsPaginationActive"
                  previousClassName="filtroStudentsPaginationItem"
                  nextClassName="filtroStudentsPaginationItem"
                  disabledClassName="filtroStudentsPaginationDisabled"
                />
              )}
            </Flex>
          </Box>

          <style>{`
            .filtroStudentsPagination {
              display: flex;
              list-style: none;
              gap: 4px;
              margin: 0;
              padding: 0;
            }

            .filtroStudentsPaginationItem {
              display: flex;
              align-items: center;
              justify-content: center;
              min-width: 36px;
              height: 36px;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              color: #4a5568;
              transition: all 0.2s;
            }

            .filtroStudentsPaginationItem:hover {
              background-color: #f7fafc;
              border-color: #cbd5e0;
            }

            .filtroStudentsPaginationItem a {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              height: 100%;
              padding: 0 12px;
            }

            .filtroStudentsPaginationActive {
              background-color: #574feb !important;
              border-color: #574feb !important;
              color: white !important;
            }

            .filtroStudentsPaginationActive a {
              color: white !important;
            }

            .filtroStudentsPaginationDisabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          `}</style>
        </>
      )}

      <Modal isOpen={isParentOpen} onClose={onParentClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Acudiente</ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            {parentLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                <ParentSection title="Madre" parent={parentData?.madre} />
                <ParentSection title="Padre" parent={parentData?.padre} />
                <ParentSection title="Otro" parent={parentData?.otro} />
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onParentClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isReqOpen} onClose={onReqClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Requisitos</ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            {!reqSelected?.length ? (
              <Text>No hay requisitos.</Text>
            ) : (
              <Stack spacing={3}>
                {reqSelected.map((req) => {
                  const names = getRequirementNames(req);

                  return (
                    <Box key={req.id} p={3} bg="gray.50" rounded="md">
                      <Text>
                        <b>Estado:</b> {translateReqStatus(req.status)}
                      </Text>

                      <Divider my={2} />

                      {names.map((name, idx) => (
                        <Text key={`${req.id}-${idx}`}>• {name}</Text>
                      ))}

                      {req.file?.url ? (
                        <Box mt={2}>
                          <Link href={req.file.url} target="_blank" rel="noreferrer">
                            <Button size="xs">Abrir archivo</Button>
                          </Link>
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onReqClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPayOpen} onClose={onPayClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Pagos</ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            {!paySelected?.length ? (
              <Text>No hay pagos.</Text>
            ) : (
              <Stack spacing={3}>
                {paySelected.map((p) => (
                  <Box key={p.id} p={3} bg="gray.50" rounded="md">
                    <Text>
                      <b>Monto:</b> {p.amount}
                    </Text>

                    <Text>
                      <b>Fecha:</b> {p.payment_date ? dayjs(p.payment_date).format('YYYY-MM-DD') : 'N/A'}
                    </Text>
                    <Button
                      size="xs"
                      colorScheme="purple"
                      mt={3}
                      onClick={() => {
                        setSelectedPaymentForSubjects(p);
                        onPayClose();

                        setTimeout(() => {
                          onEnrollSubjectsOpen();
                        }, 100);
                      }}
                    >
                      Matricular Materias
                    </Button>
                    <Divider my={2} />

                    <Text>
                      <b>Conceptos:</b>
                    </Text>

                    {(p.other_payments || []).length ? (
                      (p.other_payments || []).map((c) => (
                        <Text key={c.id}>• {c.name}</Text>
                      ))
                    ) : (
                      <Text>N/A</Text>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPayClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isAbsOpen} onClose={onAbsClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Inasistencias {absStudent?.name ? `- ${absStudent.name}` : ''}
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody fontSize="sm">
            {absLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : !absences?.length ? (
              <Text>No hay inasistencias registradas.</Text>
            ) : (
              <Stack spacing={3}>
                {absences.map((a) => (
                  <Box
                    key={a.id}
                    p={3}
                    bg={a.excused ? 'green.50' : 'red.50'}
                    borderWidth="1px"
                    borderColor={a.excused ? 'green.200' : 'red.200'}
                    rounded="md"
                  >
                    <Flex justify="space-between" align="start" gap={3}>
                      <Box>
                        <HStack mb={2} spacing={2}>
                          <Badge colorScheme={a.excused ? 'green' : 'red'}>
                            {a.excused ? 'Excusada' : 'Sin excusa'}
                          </Badge>

                          {a.liveClassId ? (
                            <Badge colorScheme="gray">
                              Clase #{a.liveClassId}
                            </Badge>
                          ) : null}
                        </HStack>

                        <Text>
                          <b>Materia:</b> {a.subjectName || 'N/A'}
                        </Text>

                        <Text>
                          <b>Clase:</b> {a.classTitle || 'N/A'}
                        </Text>

                        <Text>
                          <b>Fecha:</b> {a.date_time ? dayjs(a.date_time).format('YYYY-MM-DD HH:mm') : 'N/A'}
                        </Text>

                        <Text mt={1}>
                          <b>Detalles:</b> {a.details || 'N/A'}
                        </Text>

                        {a.excused ? (
                          <Box mt={2}>
                            <Text>
                              <b>Excusa:</b> {a.excuse?.description || 'Sin descripción'}
                            </Text>

                            {a.excuse?.excuse_start ? (
                              <Text>
                                <b>Inicio excusa:</b> {dayjs(a.excuse.excuse_start).format('YYYY-MM-DD HH:mm')}
                              </Text>
                            ) : null}

                            {a.excuse?.excuse_finish ? (
                              <Text>
                                <b>Fin excusa:</b> {dayjs(a.excuse.excuse_finish).format('YYYY-MM-DD HH:mm')}
                              </Text>
                            ) : null}
                          </Box>
                        ) : null}
                      </Box>

                      <Button
                        size="xs"
                        colorScheme="red"
                        isLoading={deletingAbsId === a.id}
                        onClick={() => deleteAbsence(a.id)}
                      >
                        Eliminar
                      </Button>
                    </Flex>
                  </Box>
                ))}
              </Stack>
            )}
          </ModalBody>

          <ModalFooter>
            <Flex w="100%" justify="space-between" align="center" gap={3} flexWrap="wrap">
              <HStack spacing={3}>
                <Select
                  placeholder="Periodo"
                  value={selectedAbsencePeriod}
                  onChange={(e) => setSelectedAbsencePeriod(e.target.value)}
                  maxW="180px"
                  size="sm"
                >
                  <option value="P1">Periodo 1</option>
                  <option value="P2">Periodo 2</option>
                  <option value="P3">Periodo 3</option>
                  <option value="P4">Periodo 4</option>
                </Select>

                <Button
                  colorScheme="purple"
                  onClick={sendAbsenceReport}
                  isLoading={sendingAbsenceReport}
                  isDisabled={!absStudent?.id || absLoading || !selectedAbsencePeriod}
                >
                  Enviar reporte
                </Button>
              </HStack>

              <Button onClick={onAbsClose}>
                Cerrar
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isNotesOpen} onClose={onNotesClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Notas {notesStudent?.name ? `- ${notesStudent.name}` : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            <Text mb={2} color="gray.600">
              Observaciones internas del estudiante (visibles solo para administración).
            </Text>
            <Textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              placeholder="Escribe una descripción o nota sobre el estudiante..."
              rows={6}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onNotesClose}>
              Cancelar
            </Button>
            <Button colorScheme="purple" onClick={handleSaveNotes} isLoading={savingNotes}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AttendanceReportModal
        isOpen={isAttendanceOpen}
        onClose={onAttendanceClose}
        filters={filters}
        students={students}
      />
      <EnrollSubjectsFromPaymentModal
        isOpen={isEnrollSubjectsOpen}
        onClose={onEnrollSubjectsClose}
        student={payStudentSelected}
        payment={selectedPaymentForSubjects}
        payments={paySelected}
        setPayments={setPaySelected}
      />
    </Box>
  );
}

export default FiltroStudents;